<script>
  import PharmacyOverpanelResults from '../PharmacyOverpanelResults/PharmacyOverpanelResults.svelte';
  import UseClickOff from '../../utils/UseClickOff.svelte';
  import InterceptClick from '../../utils/InterceptClick.svelte';
  import { SPRITESHEET_URL } from '../../utils/constants.ts';
  import { KEYS } from '../../utils/constants';
  import searchValue from '../../stores/search';
  import { filteredSearch } from '../../utils/search';
  import getClosest from '../../utils/closest-string';
  import { trackEvent } from '../../utils/umami-events.js';

  let {
    parentComponent,
    inputPlaceHolder,
    searchInstructions,
    buttonText,
    searchingText,
    searchCompleteTemplate,
    errorText,
    emptyErrorText,
    nationwideHeading,
    locationHeadingTemplate,
    pharmacyOverpanelResults,
    centerAligned,
    filters,
    dataTestId = 'pharmacy-search',
    lang = 'en',
  } = $props();
  const { ARROW_DOWN, ARROW_UP, ESCAPE } = KEYS;

  let searchResults = $state([]);
  let searchResultsLabel = $state();
  let previousResults = {};
  let controller;
  let container = $state();
  let isError = $state(false);
  let input = $state();
  let selected = $state();
  let inputFocused = $state(false);
  let resultsContainer = $state();
  let showInputEmptyMessage = $state(false);
  let resultTiles = $state([]);  
  let typeaheadOpen = $derived(inputFocused || selected !== undefined);

  const resultsId = 'pharmacy-search-overpanel-results-list';

  searchValue.subscribe((value) => {
    selected = undefined;
    if (!value) searchResults.length = 0;
    if (searchResults.length) inputFocused = true;
  });

  // use time to only log complete search terms
  let timer, loggedValue;
  const handleAnalyticsEvent = () => {
    if (loggedValue !== $searchValue) {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        loggedValue = $searchValue;
        trackEvent(`${parentComponent}-pharmacy-search`, { search: $searchValue });
      }, 1000);
    }
  };

  const handleFocus = () => {
    typeaheadOpen = true;
  };

  const handleBlur = () => {
    showInputEmptyMessage = false;
    inputFocused = false;
    selected = undefined;
    typeaheadOpen = false;
  };

  const handleKeydown = (e) => {
    if (![ARROW_UP, ARROW_DOWN, ESCAPE].includes(e.key)) return;

    if (e.key === ESCAPE) {
      if (!$searchValue) {
        handleBlur();
        input.blur();
      } else {
        $searchValue = '';
      }

      e.stopPropagation();
      return;
    }

    if (!searchResults.length) return;

    if (selected === undefined) {
      selected = -1;
    }

    selected += e.key === ARROW_DOWN ? 1 : -1;
    if (selected < 0) selected = searchResults.length - 1;
    selected %= searchResults.length;

    e.preventDefault();
  };

  const handleExpandedClick = () => {
    if (input) input.focus();
  };

  const handleSearch = async (e) => {
    const value = e.target.value;
    showInputEmptyMessage = false;

    if (controller) {
      controller.abort();
    }

    isError = false;

    if (!value) return;

    controller = new AbortController();

    let data;
    searchResultsLabel = searchingText;

    const res = await filteredSearch({
      value: e.target.value,
      controller,
      filters: filters || 'nationwide',
    }).catch((err) => {
      if (err.name !== 'AbortError') {
        return err;
      }
    });

    if (!res || res.status > 200) {
      return;
    }

    data = await res.json();

    function groupByLocation(array) {
      return array.reduce((acc, obj) => {
        let property = obj['_tags'];
        if (property === 'nationwide') {
          property = nationwideHeading;
        } else {
          property = locationHeadingTemplate.replace(/{{LOCATION}}/, property);
        }
        acc[property] = acc[property] || [];
        acc[property].push(obj);
        return acc;
      }, {});
    }

    if (data && data.hits) {
      // if results were returned cache them
      // if no results were returned find last closest match
      if (data.hits.length) {
        const emRegex = /<(\/?)em>/g;
        data.hits = data.hits.map((hit) => {
          try {
            hit.htmlName = hit._highlightResult.name.value.replace(emRegex, '<$1strong>');
          } catch {
            hit.htmlName = hit.name;
          }
          hit.url = `${lang === 'es' ? lang : ''}/pharmacy-near-me/${hit.slug}`;
          return hit;
        });
        previousResults[value] = data;
      } else {
        const closest = getClosest(value, Object.keys(previousResults));
        if (closest) {
          data = previousResults[closest];
        } else {
          isError = true;
        }
      }

      const groupedSearchResults = groupByLocation(data.hits);
      searchResults.length = 0;
      searchResults = Object.entries(groupedSearchResults);

      if (searchResults.length) {
        searchResultsLabel = searchCompleteTemplate.replace(/{{NUMBER}}/, searchResults.length);
      } else {
        searchResultsLabel = errorText;
      }
    }

    if ($searchValue.length > 3) {
      handleAnalyticsEvent();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const firstResult = searchResults.length && searchResults[0][1][selected || 0];

    if (!firstResult) {
      if (!$searchValue) showInputEmptyMessage = true;
      return;
    }

    if (loggedValue !== $searchValue) {
      trackEvent(`${parentComponent}-pharmacy-search`, { search: $searchValue });
    }

    window.location.href = firstResult.url;
  };
</script>

{#if typeaheadOpen}
  <UseClickOff {container} onClickOffHandler={handleBlur} />
  <InterceptClick parent={resultsContainer} clickHandlerCallback={handleExpandedClick} />
{/if}

{#if inputPlaceHolder && buttonText && pharmacyOverpanelResults}
  <div class="max-w-6xl mx-auto">
    <div bind:this={container} class="group relative max-w-152" class:mx-auto={centerAligned}>
      <form
        data-testid={dataTestId}
        class="flex flex-row relative w-full text-gray-gunmetal items-center border-12 rounded-t-lg border-b-0 z-10 pb-1"
        class:border-transparent={!typeaheadOpen}
        class:border-blue-navy={typeaheadOpen}
        class:bg-blue-navy={typeaheadOpen}
        class:mx-auto={centerAligned}
        onsubmit={handleSubmit}
      >
        <div
          class="relative w-full"
          data-testid="{dataTestId}-combobox"
          role="combobox"
          aria-expanded={!!searchResults.length}
          aria-controls={resultsId}
          aria-haspopup="listbox"
        >
          <div aria-live="assertive" aria-label={searchResultsLabel} data-testid="{dataTestId}-combobox-status"></div>
          <input
            class="inline w-full rounded border border-gray-gunmetal bg-white py-2 pl-10 pr-4 leading-loose
            text-base font-normal placeholder-gray-warm focus:relative focus:outline-hidden focus:shadow-focus
            active:shadow-active active:relative"
            class:animate-shake={showInputEmptyMessage}
            data-testid="{dataTestId}-input"
            type="search"
            placeholder={inputPlaceHolder}
            onfocus={handleFocus}
            oninput={handleSearch}
            onkeydown={handleKeydown}
            bind:this={input}
            bind:value={$searchValue}
            autocomplete="off"
            aria-autocomplete="list"
            aria-label={searchInstructions}
            aria-activedescendant={selected !== undefined ? `search-result-${selected}` : ''}
          />
          <svg
            class="absolute top-4 left-4 w-4 h-4 fill-current"
            class:animate-shake={showInputEmptyMessage}
            role="presentation"
          >
            <use xlink:href="{SPRITESHEET_URL}magnifying-glass" />
          </svg>
        </div>
        <button
          type="submit"
          class="flex-1 hidden sm:block ml-3 rounded border-t border-blue-cerulean bg-blue-cerulean py-2 px-4
          text-center text-base leading-loose font-semibold text-white hover-hover:hover:bg-blue-darkish hover-hover:hover:border-blue-darkish
          active:bg-blue-navy active:border-blue-navy whitespace-nowrap focus:relative focus:outline-hidden
          focus:shadow-focus active:shadow-active active:relative"
          onfocus={handleFocus}
        >
          {buttonText}
        </button>
      </form>
      {#if showInputEmptyMessage}
        <div
          class="border-12 border-b-0"
          class:text-blue-navy={!typeaheadOpen}
          class:text-white={typeaheadOpen}
          class:bg-blue-navy={typeaheadOpen}
          class:border-blue-navy={typeaheadOpen}
          class:border-transparent={!typeaheadOpen}
          role="alert"
        >
          {emptyErrorText}
        </div>
      {/if}
      <div
        class="absolute bg-gray-mercury left-0 mt-0 z-40 w-full border-12 border-blue-navy rounded-b-lg"
        class:hidden={!typeaheadOpen}
        class:block={typeaheadOpen}
        bind:this={resultsContainer}
      >
        <PharmacyOverpanelResults
          {...pharmacyOverpanelResults}
          id={resultsId}
          {resultTiles}
          {searchResults}
          {selected}
          {isError}
          {parentComponent}
        />
      </div>
    </div>
  </div>
{/if}
